import styled from "styled-components";
import { ArrowRightLeft, ChevronDown, CreditCard, Landmark, Network } from "lucide-react";
import { IconVisa } from "@ui/icons/IconVisa";
import { IconMastercard } from "@ui/icons/IconMastercard";
import { feeCurrencyText, feeMoney, feeNumber, feePercent, type InterchangeData, type InterchangeFee } from "./interchangeData";
import { useTranslation } from "react-i18next";

const Root = styled.div`
	display: flex; flex-direction: column; gap: 16px; min-width: 0; color: #344054; font-size: 13px;
	container-type: inline-size;
`;
const Summary = styled.section`
	border: 1px solid #e1e6ef; border-radius: 12px; background: #fff; overflow: hidden;
	.overview { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 20px; padding: 20px 22px; }
	.payment { display: flex; align-items: center; gap: 14px; min-width: 0; }
	.logo { display: flex; align-items: center; justify-content: center; width: 64px; height: 46px; flex-shrink: 0; border-radius: 8px; background: #f6f7fa; }
	h3 { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin: 0 0 6px; font-size: 14px; }
	.transaction-type { font-size: 11px; font-weight: 400; color: #667085; background: #f2f4f7; border-radius: 4px; padding: 3px 6px; }
	.payment-amount { color: #667085; font-size: 12px; line-height: 1.6; }
	.payment-amount strong { color: #344054; font-weight: 500; }
	p { margin: 5px 0 0; color: #667085; font-size: 12px; line-height: 1.6; }
	.total { margin-left: auto; text-align: right; }
	.total > span { display: block; color: #667085; font-size: 11px; margin-bottom: 3px; }
	.total strong { font-size: 28px; font-weight: 600; letter-spacing: -.6px; font-variant-numeric: tabular-nums; }
	.total p { color: #5365ad; }
	@container (max-width: 520px) { .overview { padding: 16px; } .total { width: 100%; margin: 0; text-align: left; } }
`;
const Metrics = styled.div`
	display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); border-top: 1px solid #e9edf3; background: #fafbfe;
	> div { min-width: 0; padding: 14px 22px; }
	> div + div { border-left: 1px solid #e9edf3; }
	span { display: flex; align-items: center; gap: 7px; color: #667085; font-size: 11px; }
	strong { display: block; margin-top: 6px; font-size: 16px; font-weight: 600; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
	@container (max-width: 520px) { grid-template-columns: 1fr; > div { padding: 12px 16px; display: flex; align-items: center; justify-content: space-between; gap: 12px; } > div + div { border-left: 0; border-top: 1px solid #e9edf3; } strong { margin: 0; } }
`;
const Breakdown = styled.div`
	border: 1px solid #e1e6ef; border-radius: 12px; overflow: hidden; background: #fff;
`;
const Group = styled.section`
	min-width: 0;
	& + & { border-top: 1px solid #e1e6ef; }
	> header { display: flex; align-items: center; gap: 10px; padding: 14px 20px; background: #fafbfc; }
	> header > svg { color: #7b88a1; flex-shrink: 0; }
	h3 { margin: 0; font-size: 13px; font-weight: 600; }
	> header span { display: block; margin-top: 3px; color: #667085; font-size: 11px; }
	> p { padding: 0 20px 14px; margin: 0; color: #667085; }
`;
const Fee = styled.div`
	border-top: 1px solid #edf0f5;
	.fee-row { display: grid; grid-template-columns: minmax(0, 1fr) 140px 120px 14px; gap: 14px; align-items: center; padding: 14px 20px; }
	.fee-name { font-size: 12px; line-height: 1.6; overflow-wrap: anywhere; }
	.fee-amount { text-align: right; font-size: 13px; font-weight: 600; font-variant-numeric: tabular-nums; white-space: nowrap; }
	.rate { color: #667085; font-size: 11px; text-align: right; line-height: 1.5; }
	summary { cursor: pointer; list-style: none; transition: background-color .15s; }
	summary::-webkit-details-marker { display: none; }
	summary:hover { background: #f7f9fd; }
	summary:focus-visible { outline: 2px solid #6374d9; outline-offset: -2px; }
	.chevron { color: #98a2b3; transition: transform .18s; }
	details[open] .chevron { transform: rotate(180deg); }
	dl { display: grid; grid-template-columns: 120px minmax(0, 1fr); gap: 8px 12px; margin: 0 20px 14px; padding: 14px; border-radius: 8px; background: #f8fafc; font-size: 12px; line-height: 1.6; }
	dt { color: #667085; } dd { margin: 0; overflow-wrap: anywhere; }
	@container (max-width: 640px) {
		.fee-row { grid-template-columns: minmax(0, 1fr) auto 14px; gap: 5px 10px; padding: 12px 16px; }
		.fee-name { grid-column: 1; grid-row: 1; } .rate { grid-column: 1; grid-row: 2; text-align: left; }
		.fee-amount { grid-column: 2; grid-row: 1 / span 2; } .chevron { grid-column: 3; grid-row: 1 / span 2; }
	}
	@container (max-width: 420px) { dl { grid-template-columns: 1fr; gap: 4px; margin: 0 16px 12px; } dd { margin-bottom: 6px; } }
	@media (prefers-reduced-motion: reduce) { summary, .chevron { transition: none; } }
`;
const Badge = styled.span`
	display: inline-block; padding: 2px 7px; border-radius: 5px; background: #fff4dd; color: #8b6415;
	font-size: 11px; font-weight: 500; margin-left: 8px; vertical-align: middle;
`;

function FeeRow({ fee }: { fee: InterchangeFee }) {
	const { t, i18n } = useTranslation();
	const locale = (i18n.resolvedLanguage || i18n.language).startsWith("ru") ? "ru-RU" : "en-GB";
	const metadata = [
		[t("interchange.metadata.basis"), feeCurrencyText(fee.basis)], [t("interchange.metadata.source"), feeCurrencyText(fee.source)], [t("interchange.metadata.note"), feeCurrencyText(fee.note)],
		[t("interchange.metadata.id"), fee.id], [t("interchange.metadata.service"), feeCurrencyText(fee.service)], [t("interchange.metadata.eventId"), fee.eventId === "—" ? undefined : fee.eventId],
		[t("interchange.metadata.rate"), fee.rate != null ? feePercent(fee.rate * 100, locale) : undefined],
		[t("interchange.metadata.fixed"), fee.flat != null ? feeMoney(fee.flat, "EUR", locale) : undefined],
		[t("interchange.metadata.maximum"), fee.cap != null ? feeMoney(fee.cap, "EUR", locale) : undefined],
		[t("interchange.metadata.minimum"), fee.min != null ? feeMoney(fee.min, "EUR", locale) : undefined],
	].filter((entry): entry is [string, string] => typeof entry[1] === "string" && entry[1] !== "");
	const row = <>
		<span className="fee-name">{feeCurrencyText(fee.name)}{fee.isIndicative && <Badge>{t("interchange.indicative")}</Badge>}</span>
		<span className="rate">{feeCurrencyText(fee.rateDescription)}</span>
		<span className="fee-amount">{feeMoney(fee.amountEur, "EUR", locale)}</span>
		{metadata.length > 0 && <ChevronDown className="chevron" size={14} aria-hidden="true" />}
	</>;
	return <Fee>{metadata.length > 0
		? <details><summary className="fee-row" title={t("interchange.feeDetails")}>{row}</summary><dl>{metadata.map(([label, value]) => <div key={label} style={{ display: "contents" }}><dt>{label}</dt><dd>{value}</dd></div>)}</dl></details>
		: <div className="fee-row">{row}</div>}
	</Fee>;
}

export function InterchangeDetails({ data }: { data: InterchangeData }) {
	const { t, i18n } = useTranslation();
	const locale = (i18n.resolvedLanguage || i18n.language).startsWith("ru") ? "ru-RU" : "en-GB";
	const groups = [
		{ key: "interchange", total: data.totalInterchangeEur, rows: data.interchange, icon: Landmark },
		{ key: "scheme", total: data.totalSchemeFeesEur, rows: data.schemeFees, icon: Network },
		{ key: "switching", total: data.totalSwitchingFeesEur, rows: data.switchingFees, icon: ArrowRightLeft },
	];
	const network = data.network.toLowerCase() === "mastercard" ? "Mastercard" : data.network.toLowerCase() === "visa" ? "Visa" : data.network;
	return <Root>
		<Summary aria-label={t("interchange.totalAria")}>
			<div className="overview">
				<div className="payment">
					<span className="logo" aria-hidden="true">{network === "Visa" ? <IconVisa width={58} height={28} /> : network === "Mastercard" ? <IconMastercard width={54} height={38} /> : <CreditCard size={26} />}</span>
					<div><h3>{network}<span className="transaction-type">{data.transactionType}</span></h3>
						<div className="payment-amount">{t("interchange.payment")} <strong>{feeMoney(data.amountOriginal, data.currency, locale)}</strong></div>
						{(data.currency !== "EUR" || data.fxRate !== 1) && <p>{t("interchange.inEur")}: {feeMoney(data.amountEur, "EUR", locale)} · {t("interchange.exchangeRate")}: {feeNumber(data.fxRate, 0, locale)}</p>}
					</div>
				</div>
				<div className="total"><span>{t("interchange.totalFee")}</span><strong>{feeMoney(data.totalEur, "EUR", locale)}</strong><p>{feePercent(data.totalPercent, locale)} {t("interchange.ofPaymentAmount")}</p></div>
			</div>
			<Metrics>{groups.map(({ key, total, icon: Icon }) => <div key={key}><span><Icon size={14} aria-hidden="true" />{t(`interchange.groups.${key}.title`)}</span><strong>{feeMoney(total, "EUR", locale)}</strong></div>)}</Metrics>
		</Summary>
		<Breakdown>{groups.map(({ icon: Icon, ...group }) => <Group key={group.key} aria-label={t(`interchange.groups.${group.key}.title`)}>
			<header><Icon size={17} aria-hidden="true" /><div><h3>{t(`interchange.groups.${group.key}.title`)}</h3><span>{t(`interchange.groups.${group.key}.description`)}</span></div></header>
			{group.rows.length ? group.rows.map((fee, index) => <FeeRow key={`${fee.id ?? fee.name}-${index}`} fee={fee} />) : <p>{t("interchange.emptyCategory")}</p>}
		</Group>)}</Breakdown>
	</Root>;
}
